import { PUBLIC_POCKETBASE_URL } from '$env/static/public';
import { stripHiddenMeta, stripMeta } from '$lib/resources-strip';
import { handleApiError } from '@astlai/common/util/error-handling';
import type {
	DocumentDTO,
	ResourceDTO,
	ResourcesResponse,
	RetrievalContext,
	TenantStats
} from 'src/types/llm';

export class RAGService {
	private readonly baseURL: string;

	constructor(
		pbUrl = PUBLIC_POCKETBASE_URL,
		private authToken?: string
	) {
		this.baseURL = `${pbUrl}/proxy`;
	}

	private doFetch(path: string, init: RequestInit = {}) {
		const additionalInit: RequestInit = this.authToken
			? { headers: { Authorization: `Bearer ${this.authToken}` } }
			: { credentials: 'include' };

		return fetch(`${this.baseURL}/${path}`, {
			...init,
			...additionalInit,
			headers: { ...init.headers, ...additionalInit.headers }
		});
	}

	async getDocument(documentId: number): Promise<DocumentDTO> {
		const response = await this.doFetch(`documents/${documentId}`);
		await handleApiError(response);
		return stripHiddenMeta(await response.json());
	}

	async getResource(resourceId: number): Promise<ResourceDTO> {
		const response = await this.doFetch(`resource/${resourceId}`);
		await handleApiError(response);
		return stripMeta(await response.json());
	}

	async getResources(
		tenantId: string,
		page = 1,
		perPage = 20,
		{
			search = '',
			sortBy = 'date',
			sortDir = 'desc',
			resourceType = ''
		}: { search?: string; sortBy?: string; sortDir?: string; resourceType?: string } = {}
	): Promise<ResourcesResponse> {
		const params = new URLSearchParams({ page: page.toString(), per_page: perPage.toString() });
		if (search) {
			params.set('search', search);
		}
		if (sortBy) {
			params.set('sort_by', sortBy);
		}
		if (sortDir) {
			params.set('sort_dir', sortDir);
		}
		if (resourceType) {
			params.set('resource_type', resourceType);
		}

		const response = await this.doFetch(`${tenantId}/resources?${params}`);
		await handleApiError(response);
		const data: ResourcesResponse = await response.json();
		return { ...data, resources: data.resources.map(stripMeta) };
	}

	async getStats(tenantId: string): Promise<TenantStats> {
		const response = await this.doFetch(`${tenantId}/stats`);
		await handleApiError(response);
		return response.json();
	}

	async extractPdf(file: File): Promise<{ text: string; block_count: number }> {
		const formData = new FormData();
		formData.append('file', file);
		const response = await this.doFetch('extract-pdf', { method: 'POST', body: formData });
		await handleApiError(response);
		return response.json();
	}

	async getRetrievalContext(
		docIds: number[],
		chunkIds: number[],
		tenant: string
	): Promise<RetrievalContext> {
		const response = await this.doFetch('retrieval-context', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				doc_ids: docIds,
				chunk_ids: chunkIds,
				tenant
			})
		});
		await handleApiError(response);
		return response.json();
	}
}

export const ragService = new RAGService();
